import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { api, type EmailImapConnectInput } from '@/lib/api';
import { useInitEmailOAuth, useConnectEmailImap } from '../hooks/useConnectAccount';
import { useSyncAccountStream, formatSyncProgress } from '../hooks/useMailbox';

interface ConnectAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected?: () => void;
}

type View = 'choose' | 'manual' | 'syncing';
type Security = 'ssl_tls' | 'starttls' | 'none';

// v1 ships app-password IMAP/SMTP only (covers Gmail/Outlook via app-passwords).
// One-click OAuth ("Connect Gmail/Outlook") is post-v1 — it needs the Google
// restricted-scope verification + Graph app-registration. Flip to true once the
// OAuth app registrations are configured. The OAuth backend code stays in place.
const EMAIL_OAUTH_ENABLED = false;

const EMPTY_MANUAL = {
  emailAddress: '',
  displayName: '',
  // POP3 is deferred from the UI — new manual accounts are always IMAP. The
  // server transport model still recognizes 'pop3' (sync throws
  // POP3_SYNC_NOT_IMPLEMENTED), so re-enabling is a UI change plus a POP3 sync
  // client. See the "Deferred: POP3" note in docs/api-email.md.
  incomingProtocol: 'imap' as 'imap' | 'pop3',
  incomingHost: '',
  incomingPort: 993,
  incomingSecurity: 'ssl_tls' as Security,
  outgoingHost: '',
  outgoingPort: 587,
  outgoingSecurity: 'starttls' as Security,
  username: '',
  password: '',
};

export function ConnectAccountDialog({
  open,
  onOpenChange,
  onConnected,
}: ConnectAccountDialogProps) {
  const [view, setView] = useState<View>(EMAIL_OAUTH_ENABLED ? 'choose' : 'manual');
  const [manual, setManual] = useState({ ...EMPTY_MANUAL });
  const [error, setError] = useState<string | null>(null);
  const [autoconfigBusy, setAutoconfigBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // The just-connected account we're streaming the first sync for.
  const [syncingAccount, setSyncingAccount] = useState<{
    accountId: string;
    emailAddress: string;
  } | null>(null);

  const initOAuth = useInitEmailOAuth();
  const connectImap = useConnectEmailImap();
  const syncStream = useSyncAccountStream();
  const isLoading = initOAuth.isPending || connectImap.isPending;

  const reset = () => {
    setView(EMAIL_OAUTH_ENABLED ? 'choose' : 'manual');
    setManual({ ...EMPTY_MANUAL });
    setError(null);
    setShowPassword(false);
    setSyncingAccount(null);
  };

  const close = () => {
    if (isLoading) return;
    reset();
    onOpenChange(false);
  };

  // Leave the first-sync step: the account is already connected, so this always
  // proceeds to the inbox. If the stream is still running the background
  // scheduler finishes the rest — the user just stops watching.
  const finishSync = () => {
    reset();
    onOpenChange(false);
    onConnected?.();
  };

  const handleOAuth = async (provider: 'gmail' | 'microsoft') => {
    setError(null);
    try {
      await initOAuth.mutateAsync(provider);
      // Popup opened; connection list refreshes via BroadcastChannel.
      close();
      onConnected?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start sign-in');
    }
  };

  // Prefill server settings from Thunderbird-style autoconfig (never auto-submits).
  const handleEmailBlur = async () => {
    const email = manual.emailAddress.trim();
    if (!email.includes('@') || manual.incomingHost) return;
    setAutoconfigBusy(true);
    try {
      const { suggestion } = await api.getEmailAutoconfig(email);
      if (suggestion) {
        setManual((m) => ({
          ...m,
          username: m.username || email,
          incomingProtocol: 'imap',
          incomingHost: suggestion.incomingHost,
          incomingPort: suggestion.incomingPort,
          incomingSecurity: suggestion.incomingSecurity,
          outgoingHost: suggestion.outgoingHost,
          outgoingPort: suggestion.outgoingPort,
          outgoingSecurity: suggestion.outgoingSecurity,
        }));
      } else {
        setManual((m) => ({ ...m, username: m.username || email }));
      }
    } catch {
      /* best-effort; user can fill manually */
    } finally {
      setAutoconfigBusy(false);
    }
  };

  const handleManualSubmit = async () => {
    setError(null);
    const payload: EmailImapConnectInput = {
      emailAddress: manual.emailAddress.trim(),
      displayName: manual.displayName.trim() || undefined,
      incomingProtocol: manual.incomingProtocol,
      incomingHost: manual.incomingHost.trim(),
      incomingPort: Number(manual.incomingPort),
      incomingSecurity: manual.incomingSecurity,
      outgoingHost: manual.outgoingHost.trim(),
      outgoingPort: Number(manual.outgoingPort),
      outgoingSecurity: manual.outgoingSecurity,
      username: manual.username.trim(),
      password: manual.password,
    };
    try {
      const account = await connectImap.mutateAsync(payload);
      // Connected. Stay open and stream the first sync so a big mailbox shows
      // live "downloading message N of M" progress instead of dropping the user
      // on a dead-looking empty inbox (Thunderbird-style first-run download).
      setSyncingAccount({ accountId: account.accountId, emailAddress: account.emailAddress });
      setView('syncing');
      void syncStream.start(account.accountId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not connect. Check your settings.');
    }
  };

  const manualValid =
    manual.emailAddress.includes('@') &&
    manual.incomingHost.trim() &&
    manual.outgoingHost.trim() &&
    manual.username.trim() &&
    manual.password;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (view === 'syncing' ? finishSync() : close())}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {view === 'choose'
              ? 'Connect an email account'
              : view === 'syncing'
                ? 'Setting up your mailbox'
                : 'Manual setup (IMAP/SMTP)'}
          </DialogTitle>
        </DialogHeader>

        {view === 'choose' && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Connect your existing mailbox.</p>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleOAuth('gmail')}
              disabled={isLoading}
            >
              {initOAuth.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleIcon />}
              Connect Gmail
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleOAuth('microsoft')}
              disabled={isLoading}
            >
              {initOAuth.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <MicrosoftIcon />
              )}
              Connect Outlook
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-12"
              onClick={() => {
                setError(null);
                setView('manual');
              }}
              disabled={isLoading}
            >
              Set up manually (IMAP)
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        {view === 'manual' && (
          // Negative right margin + matching padding gives the scrollbar its own
          // gutter so it never overlaps the inputs; the small left inset keeps
          // focus rings from being clipped by overflow-x.
          //
          // A real <form autoComplete="off"> wrapper (not a bare <div>) tells the
          // browser these fields are ONE "connect a mailbox" form, not a Fias
          // sign-in. Combined with the password's autoComplete="new-password"
          // below, the browser reads this as account-creation and stops offering
          // the user's saved Fias login over the email/password fields. The
          // wrapper also enables Enter-to-submit.
          <form
            className="space-y-3 py-1 max-h-[60vh] overflow-y-auto -mr-4 pr-4 -ml-1 pl-1"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              if (manualValid && !connectImap.isPending) void handleManualSubmit();
            }}
          >
            {EMAIL_OAUTH_ENABLED && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1 -ml-2"
                onClick={() => setView('choose')}
                disabled={connectImap.isPending}
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}

            <p className="text-xs text-muted-foreground">
              Connect any mailbox with your server settings. For Gmail or Outlook, enable 2-factor
              auth and paste an <span className="font-medium">app password</span> (not your normal
              password).
            </p>

            <Field label="Email address">
              <Input
                // `type="text"` + `inputMode="email"` — deliberately NOT
                // `type="email"`. Browsers ignore autoComplete="off" on a
                // recognized email field and pop their saved-email/login chooser
                // (the user's Fias login) over it. A text input with an email
                // keyboard suppresses that while keeping the mobile @-keyboard;
                // we validate the address ourselves (`emailAddress.includes('@')`),
                // so native email validation isn't relied on.
                type="text"
                inputMode="email"
                autoComplete="off"
                placeholder="you@example.com"
                value={manual.emailAddress}
                onChange={(e) => setManual((m) => ({ ...m, emailAddress: e.target.value }))}
                onBlur={handleEmailBlur}
                disabled={connectImap.isPending}
              />
              {autoconfigBusy && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Looking up server settings…
                </p>
              )}
            </Field>

            <Field label="Display name (optional)">
              <Input
                value={manual.displayName}
                onChange={(e) => setManual((m) => ({ ...m, displayName: e.target.value }))}
                disabled={connectImap.isPending}
              />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Incoming host">
                <Input
                  placeholder="imap.example.com"
                  value={manual.incomingHost}
                  onChange={(e) => setManual((m) => ({ ...m, incomingHost: e.target.value }))}
                  disabled={connectImap.isPending}
                />
              </Field>
              <Field label="Incoming port">
                <Input
                  type="number"
                  value={manual.incomingPort}
                  onChange={(e) =>
                    setManual((m) => ({ ...m, incomingPort: Number(e.target.value) }))
                  }
                  disabled={connectImap.isPending}
                />
              </Field>
            </div>

            <Field label="Incoming security">
              <SecuritySelect
                value={manual.incomingSecurity}
                onChange={(v) => setManual((m) => ({ ...m, incomingSecurity: v }))}
                disabled={connectImap.isPending}
              />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label="SMTP host">
                <Input
                  placeholder="smtp.example.com"
                  value={manual.outgoingHost}
                  onChange={(e) => setManual((m) => ({ ...m, outgoingHost: e.target.value }))}
                  disabled={connectImap.isPending}
                />
              </Field>
              <Field label="SMTP port">
                <Input
                  type="number"
                  value={manual.outgoingPort}
                  onChange={(e) =>
                    setManual((m) => ({ ...m, outgoingPort: Number(e.target.value) }))
                  }
                  disabled={connectImap.isPending}
                />
              </Field>
            </div>

            <Field label="SMTP security">
              <SecuritySelect
                value={manual.outgoingSecurity}
                onChange={(v) => setManual((m) => ({ ...m, outgoingSecurity: v }))}
                disabled={connectImap.isPending}
              />
            </Field>

            <Field label="Username">
              <Input
                autoComplete="off"
                value={manual.username}
                onChange={(e) => setManual((m) => ({ ...m, username: e.target.value }))}
                disabled={connectImap.isPending}
              />
            </Field>

            <Field label="Password / app-password">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  // `new-password` tells the browser this is a fresh credential,
                  // not a saved login to autofill (the reliable cross-browser way
                  // to suppress autofill on a password field).
                  autoComplete="new-password"
                  className="pr-9"
                  value={manual.password}
                  onChange={(e) => setManual((m) => ({ ...m, password: e.target.value }))}
                  disabled={connectImap.isPending}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={close}
                disabled={connectImap.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!manualValid || connectImap.isPending}>
                {connectImap.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {connectImap.isPending ? 'Verifying…' : 'Connect'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {view === 'syncing' && syncingAccount && (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3">
              {syncStream.syncing ? (
                <Loader2 className="h-5 w-5 mt-0.5 shrink-0 animate-spin text-primary" />
              ) : syncStream.error ? (
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-destructive" />
              ) : (
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-green-600" />
              )}
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium truncate">{syncingAccount.emailAddress}</p>
                <p className="text-sm text-muted-foreground">
                  {syncStream.syncing
                    ? syncStream.progress
                      ? `Downloading your mail — ${formatSyncProgress(syncStream.progress)}`
                      : 'Starting first sync…'
                    : syncStream.error
                      ? "Account connected. We couldn't finish the first sync — it'll keep trying in the background."
                      : `Downloaded ${syncStream.result?.messagesUpserted ?? 0} message${
                          syncStream.result?.messagesUpserted === 1 ? '' : 's'
                        }.`}
                </p>
                {!syncStream.syncing && syncStream.error && (
                  <p className="text-xs text-destructive break-words">{syncStream.error}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button onClick={finishSync}>
                {syncStream.syncing ? 'Continue in background' : 'Go to inbox'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function SecuritySelect({
  value,
  onChange,
  disabled,
}: {
  value: Security;
  onChange: (v: Security) => void;
  disabled?: boolean;
}) {
  return (
    <select
      className="w-full h-9 rounded-md border bg-background px-2 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value as Security)}
      disabled={disabled}
    >
      <option value="ssl_tls">SSL/TLS</option>
      <option value="starttls">STARTTLS</option>
      <option value="none">None (not recommended)</option>
    </select>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
